import { useCallback, useEffect, useState } from 'react';

/**
 * The element's own width in px, for the few decisions CSS container queries cannot make
 * (rendering a Sheet instead of a pane). A callback ref, for the same reason as
 * `useFillHeight`: the element may mount after a loading gate. Undefined until measured.
 */
export function useContainerWidth<T extends HTMLElement>(): [
  (node: T | null) => void,
  number | undefined,
] {
  const [node, setNode] = useState<T | null>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const ref = useCallback((el: T | null) => setNode(el), []);

  useEffect(() => {
    if (!node) return;
    const measure = () => setWidth(Math.floor(node.getBoundingClientRect().width));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return [ref, width];
}
